<!-- Responsibility: Implement the tabbed admin dashboard container.
Scope: Visual layout, tab orchestration, and delegation to subject-specific tabs. -->
<admin-dashboard-view>
  <div class="menu-overlay admin-dashboard">
    <header class="admin-header">
      <div class="hero">
        <h1>ADMIN DASHBOARD</h1>
        <p>Operational control, permissions, moderation, and live runtime policy.</p>
      </div>
      <button class="action-btn admin-back" type="button" onclick={ goBack }>← BACK TO HUB</button>
    </header>

    <nav class="admin-tabs">
      <button 
        each={ tab in tabs } 
        class="tab-btn" 
        data-active={ state.activeTab === tab.id ? 'true' : 'false' }
        onclick={ () => switchTab(tab.id) }
      >
        { tab.label }
      </button>
    </nav>

    <div class="admin-shell">
      <p if={ state.loading } class="admin-copy">Syncing administrative state...</p>

      <section if={ !state.loading && !state.isAdmin } class="admin-denied">
        <h2>Access restricted</h2>
        <p>Administrative access is reserved for authorized role holders.</p>
        <p class="admin-meta">ID: { state.uid || 'none' } · ROLE: { state.roleLabel }</p>
      </section>

      <div if={ !state.loading && state.isAdmin } class="tab-viewport">
        <admin-system-tab 
          if={ state.activeTab === 'system' } 
          state={ state } 
          on-refresh={ refreshEverything }
        ></admin-system-tab>

        <admin-users-tab 
          if={ state.activeTab === 'users' } 
          state={ state } 
          helpers={ helpers }
          on-send-invite={ sendInvite }
          on-quick-set-role={ quickSetRole }
          on-revoke-tester-grant={ revokeTesterGrant }
        ></admin-users-tab>

        <admin-commerce-tab 
          if={ state.activeTab === 'commerce' } 
          state={ state } 
          helpers={ helpers }
          on-save-plan={ saveCommercePlan }
        ></admin-commerce-tab>

        <admin-forging-tab 
          if={ state.activeTab === 'forging' } 
          state={ state } 
          helpers={ helpers }
          on-initiate={ startNewReality }
          on-select={ openExistingReality }
          on-open-ai-settings={ openPreforgeAiSettings }
          on-dismiss-notice={ dismissPreforgeBlockingNotice }
          on-brief-input={ (v) => update({ preforgeComposerBrief: v }) }
          on-process-brief={ processPreforgeBrief }
          on-unlock-form={ unlockPreforgeForm }
          on-clone-version={ cloneRealityVersion }
          on-realize={ realizePreforgeForm }
          on-reset-form={ resetPreforgeForm }
        ></admin-forging-tab>
      </div>
    </div>
  </div>

  <script>
    import { navigateState, dispatcher, sysNotify } from '../../app-states.js'
    import { authStore } from '../stores/auth-store.js'
    import { adminStore, SUPREME_ADMIN_EMAIL } from '../stores/admin-store.js'
    import { roleLabel } from '../../admin/admin-contract.ts'
    import { DEFAULT_COMMERCE_PLANS, buildPreforgeRecipeDefaults } from '../../commerce/commerce-contract.ts'
    import { adminService } from '../../services/admin-service.ts'
    import { manageRealitiesService } from '../../services/manage-realities-service.ts'
    import { adminPreforgeService } from '../../services/admin-preforge-service.ts'
    import { buildPreforgeReviewSubmission } from '../helpers/admin-preforge-form.ts'
    import { getAITelemetry } from '../../ai/telemetry.js'
    import { TOPICS } from "@anyquest/shared-core"

    import AdminSystemTab from '../components/admin/admin-system-tab.riot'
    import AdminUsersTab from '../components/admin/admin-users-tab.riot'
    import AdminCommerceTab from '../components/admin/admin-commerce-tab.riot'
    import AdminForgingTab from '../components/admin/admin-forging-tab.riot'

    import { buildPreforgePairingLabel, formatPreforgeRouteDetails } from '../helpers/admin-preforge-utils.ts'

    export default {
      components: { AdminSystemTab, AdminUsersTab, AdminCommerceTab, AdminForgingTab },

      tabs: [
        { id: 'system', label: 'SYSTEM' },
        { id: 'users', label: 'USERS' },
        { id: 'commerce', label: 'COMMERCE' },
        { id: 'forging', label: 'FORGING' }
      ],

      state: {
        activeTab: 'system',
        loading: true,
        isAdmin: false,
        isSupremeAdmin: false,
        supremeEmail: SUPREME_ADMIN_EMAIL,
        commercePlans: [],
        roleGrants: [],
        testerGrants: [],
        preforgedOffers: [],
        commerce: { planLabel: 'FREE', entitlementSource: 'default' },
        billingAudit: { events: [] },
        errorTail: [],
        preforgeComposerMode: 'form',
        preforgeComposerLocked: true,
        preforgeComposerHeading: 'Reality Forging',
        preforgeComposerCopy: 'Manage world drafts and realized offers.',
      },

      helpers: {
        principalName: (g) => g.displayName || g.email || g.uid || 'Anonymous',
        principalHandle: (g) => g.email ? `<${g.email}>` : (g.uid ? `(${g.uid.slice(0, 8)}...)` : ''),
        formatRole: (r) => String(r || 'normal').toUpperCase(),
        formatTime: (ts) => new Date(ts).toLocaleString(),
        testerGrantLabel: (g) => `${g.kind.toUpperCase()}: ${g.targetId}`,
      },

      onMounted() {
        this.adminUnsub = adminStore.subscribe(() => this.syncAdminState())
        this.syncAdminState()
        void adminStore.refresh()
        this.refreshInterval = setInterval(() => this.refreshHealth(), 30_000)
      },

      onUnmounted() {
        this.adminUnsub?.()
        if (this.refreshInterval) clearInterval(this.refreshInterval)
      },

      syncAdminState() {
        const snap = adminStore.snapshot
        this.update({
          loading: snap.status === 'loading',
          isAdmin: snap.isAdmin,
          isSupremeAdmin: snap.isSupremeAdmin,
          uid: snap.uid,
          email: snap.email,
          roleLabel: roleLabel(snap.role),
          statusLabel: snap.isAdmin ? 'Allowed' : 'Denied',
          sourceLabel: snap.source === 'firestore' ? 'Firestore' : 'Local',
          flagsSavedLabel: snap.flags?.updatedAtMs ? this.helpers.formatTime(snap.flags.updatedAtMs) : 'Never',
          flags: snap.flags,
          roleGrants: snap.roleGrants || [],
          commercePlans: snap.commercePlans || DEFAULT_COMMERCE_PLANS,
          testerGrants: snap.testerGrants || [],
          preforgedOffers: (snap.preforgedOffers || []).map(o => ({ ...o, pairingLabel: buildPreforgePairingLabel(o) })),
        })
      },

      switchTab(id) { this.update({ activeTab: id }) },
      goBack() { navigateState('hub') },
      
      async refreshEverything() {
        await adminStore.refresh()
        this.refreshHealth()
      },

      refreshHealth() {
        const telemetry = getAITelemetry()
        this.update({
          errorTail: telemetry.filter(e => !e.ok).slice(0, 5).map(e => ({
            provider: e.provider, jobType: e.jobType, error: String(e.error).slice(0, 100)
          }))
        })
      },

      // Logic Delegation to Services
      async sendInvite(email) {
        this.update({ sendingInvite: true, inviteMessage: '' })
        try {
          const result = await adminService.sendInvite(email)
          this.update({
            sendingInvite: false,
            inviteMessage: result.message || '',
          })
          return result
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error)
          this.update({
            sendingInvite: false,
            inviteMessage: message,
          })
          throw error
        }
      },
      async saveCommercePlan(plan) { await adminService.saveCommercePlan(plan) },
      async quickSetRole(grant, role) { await adminService.quickSetRole(grant, role) },
      async revokeTesterGrant(grant) { await adminService.revokeTesterGrant(grant) },

      // Forging Actions
      startNewReality() {
        this.update({
          preforgeComposerMode: 'brief',
          preforgeComposerHeading: 'Forge New Reality',
          preforgeComposerCopy: 'Describe the world concept below.',
          selectedOfferId: 'NEW',
          selectedOfferStatus: 'DRAFT',
          currentOffer: null,
          preforgeComposerBrief: ''
        })
      },

      openExistingReality(offer) {
        this.update({
          preforgeComposerMode: 'form',
          preforgeComposerLocked: true,
          preforgeComposerHeading: `Reality: ${offer.id}`,
          preforgeComposerCopy: 'Reviewing realized offer.',
          selectedOfferId: offer.id,
          selectedOfferStatus: offer.publishState.toUpperCase(),
          currentOffer: offer
        })
      },

      unlockPreforgeForm() { this.update({ preforgeComposerLocked: false }) },

      async processPreforgeBrief() {
        const brief = this.state.preforgeComposerBrief
        if (!brief) return sysNotify('Brief is empty.')
        const seq = ++this.seq || (this.seq = 1)
        this.update({ preforgeComposerMode: 'processing', preforgeProgressLines: ['Connecting to AI...'] })
        try {
          await adminPreforgeService.processBrief(brief, 
            (q) => this.update({ currentOffer: q, preforgeComposerMode: 'form', preforgeComposerLocked: false }),
            (u, m) => this.update({ currentOffer: u, preforgeMessage: m })
          )
        } catch (err) {
          this.update({ preforgeComposerMode: 'brief', preforgeBlockingNotice: formatPreforgeRouteDetails(err) })
        }
      },

      async realizePreforgeForm(values) {
        const offer = buildPreforgeReviewSubmission(values)
        await manageRealitiesService.saveOffer(offer)
        this.update({ preforgeComposerLocked: true, selectedOfferStatus: 'PUBLISHED' })
      },

      async cloneRealityVersion() {
        const newId = await manageRealitiesService.cloneVersion(this.state.currentOffer)
        this.update({ selectedOfferId: newId })
      },

      resetPreforgeForm() { this.update({ preforgeComposerMode: 'form', selectedOfferId: '', currentOffer: null }) },
      openPreforgeAiSettings() { dispatcher.dispatch(TOPICS.UI_OPEN_AI_SETTINGS) },
      dismissPreforgeBlockingNotice() { this.update({ preforgeBlockingNotice: null }) }
    }
  </script>

  <style>
    .admin-dashboard { padding: var(--space-lg); overflow-y: auto; display: grid; grid-template-rows: auto auto 1fr; gap: var(--space-md); }
    .admin-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--border); padding-bottom: var(--space-md); }
    .admin-tabs { display: flex; gap: var(--space-xs); border-bottom: 1px solid var(--border); padding-bottom: 1px; }
    .tab-btn { background: none; border: 1px solid transparent; padding: var(--space-sm) var(--space-lg); color: var(--text-muted); cursor: pointer; font-weight: bold; border-radius: 0.5rem 0.5rem 0 0; }
    .tab-btn[data-active="true"] { color: var(--gold); border: 1px solid var(--border); border-bottom-color: var(--bg); background: var(--bg); margin-bottom: -1px; }
    .admin-shell { padding: var(--space-md) 0; }
    .admin-denied { text-align: center; padding: var(--space-xl); background: var(--surface-raised); border-radius: 1rem; }
    .admin-meta { color: var(--text-muted); font-family: monospace; font-size: 0.8rem; }
  </style>
</admin-dashboard-view>
